// Security utilities and configuration
import { supabase } from "@/integrations/supabase/client";

export interface SecurityConfig {
  maxLoginAttempts: number;
  lockoutDuration: number; // in minutes
  sessionTimeout: number; // in minutes
  csrfTokenLength: number;
}

const securityConfig: SecurityConfig = {
  maxLoginAttempts: 5,
  lockoutDuration: 15,
  sessionTimeout: 720, // 12 hours
  csrfTokenLength: 32,
};

// Rate limiting for authentication attempts
class RateLimiter {
  private attempts: Map<string, { count: number; firstAttempt: number; lockedUntil?: number }> = new Map();

  isBlocked(identifier: string): boolean {
    const record = this.attempts.get(identifier);
    if (!record) return false;

    if (record.lockedUntil && Date.now() < record.lockedUntil) {
      return true;
    }

    // Reset if lockout period has passed
    if (record.lockedUntil && Date.now() >= record.lockedUntil) {
      this.attempts.delete(identifier);
      return false;
    }

    return false;
  }

  recordAttempt(identifier: string, successful: boolean): boolean {
    if (this.isBlocked(identifier)) {
      return false;
    }

    const now = Date.now();
    const record = this.attempts.get(identifier);

    if (successful) {
      // Clear attempts on successful login
      this.attempts.delete(identifier);
      return true;
    }

    if (!record) {
      this.attempts.set(identifier, { count: 1, firstAttempt: now });
    } else {
      // Reset count if more than an hour has passed
      if (now - record.firstAttempt > 60 * 60 * 1000) {
        this.attempts.set(identifier, { count: 1, firstAttempt: now });
      } else {
        record.count++;
        
        // Lock account if max attempts exceeded
        if (record.count >= securityConfig.maxLoginAttempts) {
          record.lockedUntil = now + (securityConfig.lockoutDuration * 60 * 1000);
        }
      }
    }

    return true;
  }

  getRemainingAttempts(identifier: string): number {
    const record = this.attempts.get(identifier);
    if (!record) return securityConfig.maxLoginAttempts;
    return Math.max(0, securityConfig.maxLoginAttempts - record.count);
  }

  getTimeUntilUnlock(identifier: string): number {
    const record = this.attempts.get(identifier);
    if (!record?.lockedUntil) return 0;
    return Math.max(0, record.lockedUntil - Date.now());
  }
}

export const authRateLimiter = new RateLimiter();

// Security headers configuration
export const getSecurityHeaders = () => {
  const nonce = generateNonce();
  
  return {
    'Content-Security-Policy': [
      "default-src 'self'",
      `script-src 'self' 'nonce-${nonce}' https://cdn.jsdelivr.net https://unpkg.com`,
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com",
      "img-src 'self' data: https: blob:",
      "connect-src 'self' https://*.supabase.co https://api.stripe.com wss://*.supabase.co",
      "media-src 'self'",
      "frame-src 'self' https://js.stripe.com",
      "worker-src 'self' blob:",
      "manifest-src 'self'",
      "base-uri 'self'",
      "form-action 'self'",
      "frame-ancestors 'none'",
      "upgrade-insecure-requests",
    ].join('; '),
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
    nonce,
  };
};

// Generate cryptographically secure nonce
export const generateNonce = (): string => {
  const array = new Uint8Array(securityConfig.csrfTokenLength / 2);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// CSRF token management
export const generateCSRFToken = (): string => {
  return generateNonce();
};

export const validateCSRFToken = (token: string, storedToken: string): boolean => {
  return token && storedToken && token === storedToken;
};

// Session security utilities
export const getSessionInfo = () => {
  const storage = localStorage.getItem('sb-kllsxgvvwosxlcpxqwin-auth-token');
  if (!storage) return null;

  try {
    const session = JSON.parse(storage);
    return {
      expiresAt: new Date(session.expires_at * 1000),
      isExpired: Date.now() > session.expires_at * 1000,
      timeUntilExpiry: session.expires_at * 1000 - Date.now(),
    };
  } catch {
    return null;
  }
};

// Secure logout with cleanup
export const secureLogout = async () => {
  try {
    // Clear Supabase session
    await supabase.auth.signOut();
    
    // Clear all local storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear any cached data
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(name => caches.delete(name))
      );
    }
    
    return { success: true };
  } catch (error) {
    console.error('Secure logout failed:', error);
    return { success: false, error };
  }
};

// Input sanitization
export const sanitizeInput = (input: string): string => {
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
};

// Detect suspicious activity patterns
export const detectSuspiciousActivity = (userAgent: string, ip?: string) => {
  const suspiciousPatterns = [
    /bot|crawler|spider|scraper/i,
    /curl|wget|postman/i,
    /automated|script/i,
  ];

  const isSuspicious = suspiciousPatterns.some(pattern => pattern.test(userAgent));
  
  return {
    isSuspicious,
    userAgent,
    ip,
    timestamp: Date.now(),
  };
};

export default securityConfig;